import {
  createContent,
  parseContentSource,
  type Content,
  type PartialLocaleContent,
} from '@guild/core'

import localesFile from '../../../../content/locales.json'
import en from './en.ts'

/**
 * `content/` in the browser.
 *
 * Core does no IO, so it takes a `ContentSource` already read; on the server
 * that is the disk and here it is the bundle. Every id the API carries -- a
 * fragment, a relic, a code name, an island name structure, a log event, a
 * letter body -- becomes words only through the loaders this builds, and only
 * in the reader's locale (Q12.2).
 *
 * The source locale is imported statically because it is the fallback floor.
 * Any other locale is registered below as a dynamic import, which is what makes
 * it a separate chunk Vite loads only for the players who read in it (#84):
 *
 *     const chunks = { de: () => import('./de.ts').then((module) => module.default) }
 *
 * Only `en` is served until release, so the map is empty and the app ships one
 * locale's words.
 */
const chunks: Readonly<Record<string, () => Promise<PartialLocaleContent>>> = {}

/** Which locales this build could serve at all, whatever `locales.json` says. */
export const bundledLocales: readonly string[] = ['en', ...Object.keys(chunks)]

/**
 * Build the content view, pulling in `locale`'s chunk when there is one.
 *
 * A locale with no chunk is not an error: `resolve` falls it back to the
 * source, exactly as a half-translated file falls back per entry.
 */
export async function loadContent(locale: string | undefined): Promise<Content> {
  const byLocale: Record<string, PartialLocaleContent> = { en }
  const chunk = locale === undefined ? undefined : chunks[locale]
  if (chunk !== undefined && locale !== undefined) byLocale[locale] = await chunk()
  return createContent(parseContentSource({ locales: localesFile, byLocale }))
}
